if (!customElements.get('core-technologies')) {
  class CoreTechnologies extends HTMLElement {
    constructor() {
      super();
      this._onScroll = this._onScroll.bind(this);
      this._onResize = this._onResize.bind(this);
      this._onTilesScroll = this._onTilesScroll.bind(this);
      this._raf = null;
      this._ticking = false;
      this._mobileTicking = false;
      this._totalScrollDistance = 0;
      this._scrollPerTile = 0;
      this._stickyTop = 0;
      this._bottomLock = false;
      // AAS-5264: true once the entry morph has been settled because the strip
      // left the first tile, so the cleanup runs once instead of every frame.
      this._entrySettled = false;
    }

    connectedCallback() {
      this._stickyEl = this.querySelector('.core-technologies__sticky');
      this._tilesEl = this.querySelector('.core-technologies__tiles');
      this._tile = this.querySelector('.core-technologies__tile.is-active');
      this._currentEl = this.querySelector('.core-technologies__current');
      if (!this._tile || !this._tilesEl) return;
      this._allTiles = Array.from(this._tilesEl.querySelectorAll('.core-technologies__tile'));
      this._media = this._resolveMedia(this._tile);

      this._desktopState1 = { widthPct: 100, marginLeft: 0, ar: 1440 / 851 };
      this._desktopState2 = { widthPct: 77.71, marginLeft: 30, ar: 1119 / 651 };
      this._desktopMedia1 = { w: 100, h: 100, l: 0, t: 0 };
      this._desktopMedia2 = { w: 115, h: 115, l: -7.5, t: -7.5 };

      this._mobileState1 = { widthPct: 100, marginLeft: 0, ar: 390 / 745 };
      this._mobileState2 = { widthPct: 90.5, marginLeft: 15, ar: 353 / 619 };
      this._mobileMedia1 = { w: 100, h: 100, l: 0, t: 0 };
      /* State 2 MUST match the resting `.core-technologies__tile-media` rule in
         the mobile block of section-core-technologies.css: _cleanupEntry() drops
         the inline styles at the end of the morph, so any drift between the two
         shows up as a snap on the last frame. The pair is also what keeps the
         frame's VIEWPORT rectangle constant while the tile shrinks around it —
         the entry masks a stationary image rather than moving it, which is
         load-bearing since AAS-5535 anchored the art to the frame's left edge. */
      this._mobileMedia2 = { w: 110.5, h: 120.4, l: -5.1, t: -8.7 };

      /* Same query as the `max-width: 1024px` mobile block in
         section-core-technologies.css. This said `innerWidth < 990`, so from
         990 to 1024px the desktop scroll hijack ran against the mobile layout. */
      this._mobileQuery = window.matchMedia('(max-width: 1024px)');
      this._isMobile = this._mobileQuery.matches;
      this._lastPaginationIndex = 0;
      this._rollTimeout = null;

      this._onReadMoreClick = this._onReadMoreClick.bind(this);
      this._onTileClick = this._onTileClick.bind(this);
      this._setupReadMore();

      // Setup BEFORE first _animateEntry so the chrome-height measurement
      // happens at CSS natural state (not at State 1 from the entry animation).
      if (!this._isMobile) {
        this._setupScrollHijack();
      } else {
        this._setupMobileSwipe();
      }

      this._update();

      window.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onResize, { passive: true });
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onResize);
      if (this._raf) cancelAnimationFrame(this._raf);
      this._teardownReadMore();
      this._teardownMobileSwipe();
    }

    _resolveMedia(tile) {
      const mobileMedia = tile.querySelector('.core-technologies__tile-media--mobile');
      const desktopMedia = tile.querySelector('.core-technologies__tile-media--desktop');
      const defaultMedia = tile.querySelector('.core-technologies__tile-media');
      if (this._isMobile) return mobileMedia || defaultMedia;
      return desktopMedia || defaultMedia;
    }

    _closeExpanded(tile) {
      tile.classList.remove('is-expanded');
      const btn = tile.querySelector('[data-read-more-toggle]');
      if (btn) btn.setAttribute('aria-expanded', 'false');
      const panel = tile.querySelector('.core-technologies__expanded');
      if (panel) panel.setAttribute('aria-hidden', 'true');
    }

    _setupReadMore() {
      this._readMoreBtns = this.querySelectorAll('[data-read-more-toggle]');
      this._readMoreBtns.forEach((btn) => {
        btn.setAttribute('aria-expanded', 'false');
        btn.addEventListener('click', this._onReadMoreClick);
      });

      this._allTiles.forEach((tile) => {
        tile.setAttribute('tabindex', '0');
        tile.setAttribute('role', 'button');
        // The tile ships from Liquid as role="group" with aria-roledescription
        // ="Slide" and an aria-label carrying just the heading — correct for a
        // carousel slide. Promoting it to role="button" here changes what those
        // attributes mean: an aria-label on a WIDGET replaces its content as the
        // accessible name, so the button ends up named "55mm Professional Dual
        // Strap" while displaying that heading PLUS its stats and copy. That is
        // a WCAG 2.5.3 Label in Name failure (Lighthouse:
        // label-content-name-mismatch) and it breaks speech control — the user
        // reads a phrase off the tile that the name does not contain, says it,
        // and nothing happens.
        //
        // Dropping the label lets the name compute from the tile's own text,
        // which necessarily contains everything visible. aria-roledescription
        // goes with it: "Slide" is a lie once the element is a button.
        // Both are stashed so _teardownReadMore can put the slide semantics
        // back when the mobile read-more mode is torn down.
        if (tile.hasAttribute('aria-label')) {
          tile.dataset.slideLabel = tile.getAttribute('aria-label');
          tile.removeAttribute('aria-label');
        }
        if (tile.hasAttribute('aria-roledescription')) {
          tile.dataset.slideRoledesc = tile.getAttribute('aria-roledescription');
          tile.removeAttribute('aria-roledescription');
        }
        tile.addEventListener('click', this._onTileClick);
        tile.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            tile.click();
          }
        });
      });
    }

    _teardownReadMore() {
      if (this._readMoreBtns) {
        this._readMoreBtns.forEach((btn) => {
          btn.removeEventListener('click', this._onReadMoreClick);
        });
      }
      if (this._allTiles) {
        this._allTiles.forEach((tile) => {
          tile.removeEventListener('click', this._onTileClick);
          // Restore the slide semantics taken away in _setupReadMore. Teardown
          // previously left role="button" and tabindex="0" behind, so a torn-down
          // tile stayed in the tab order announcing itself as a button with no
          // click handler left on it.
          tile.removeAttribute('tabindex');
          if (tile.dataset.slideLabel !== undefined) {
            tile.setAttribute('aria-label', tile.dataset.slideLabel);
            delete tile.dataset.slideLabel;
          }
          if (tile.dataset.slideRoledesc !== undefined) {
            tile.setAttribute('aria-roledescription', tile.dataset.slideRoledesc);
            delete tile.dataset.slideRoledesc;
          }
          tile.setAttribute('role', 'group');
        });
      }
    }

    // Anchor the panel to the CTA's red square. Called at open AND on resize
    // for any open panel (AAS-5013): the inline top/left are pixel snapshots
    // of the tile geometry, and a resize re-caps the tile / re-branches the
    // lock mode, moving the CTA — stale pixels would strand the panel at a
    // random offset in the new geometry.
    _positionExpanded(tile) {
      const expanded = tile.querySelector('.core-technologies__expanded');
      const cta = tile.querySelector('.core-technologies__cta');
      if (!expanded || !cta) return;

      const tileRect = tile.getBoundingClientRect();
      const ctaRect = cta.getBoundingClientRect();

      // Align the panel's TOP with the CTA's red square (::before) so the
      // opened layer starts exactly at the trigger. Measured, not a fixed %:
      // the heading height — and therefore the red square's position — varies
      // (a PIM title that wraps to two lines pushes it down), which a static
      // top could never track. This is what previously aligned desktop.
      const beforeH = parseFloat(getComputedStyle(cta, '::before').height) || 0;
      const probe = document.createElement('span');
      probe.style.cssText = 'font-size:0;line-height:0;';
      cta.appendChild(probe);
      const baselineY = probe.getBoundingClientRect().top;
      cta.removeChild(probe);
      expanded.style.top = `${baselineY - beforeH - tileRect.top}px`;

      if (this._isMobile) {
        // Full-width panel on mobile — leave left/right to CSS. Setting an
        // explicit left is what made the earlier mobile pixel math fragile;
        // only the top needs measuring. Sync the clip origin so the open
        // animation still sweeps from the CTA. AAS-4071
        expanded.style.left = '';
        const xPct = (ctaRect.left - tileRect.left) / tileRect.width * 100;
        expanded.style.setProperty('--clip-x', `${xPct}%`);
      } else {
        expanded.style.left = `${ctaRect.left - tileRect.left}px`;
      }
    }

    _onReadMoreClick(e) {
      e.preventDefault();
      e.stopPropagation();
      const tile = e.target.closest('.core-technologies__tile');
      if (!tile) return;

      this._positionExpanded(tile);

      tile.classList.toggle('is-expanded');
      const isExpanded = tile.classList.contains('is-expanded');
      const btn = tile.querySelector('[data-read-more-toggle]');
      const expanded = tile.querySelector('.core-technologies__expanded');
      if (btn) btn.setAttribute('aria-expanded', String(isExpanded));
      if (expanded) expanded.setAttribute('aria-hidden', String(!isExpanded));
    }

    _onTileClick(e) {
      const tile = e.target.closest('.core-technologies__tile');
      if (!tile || !tile.classList.contains('is-expanded')) return;
      if (e.target.closest('[data-read-more-toggle]')) return;
      // A click on the open red panel closes it too (AAS-5756) — except on a
      // link inside the copy, or when the click ends a text selection.
      if (e.target.closest('.core-technologies__expanded')) {
        if (e.target.closest('a')) return;
        if (String(window.getSelection())) return;
      }
      this._closeExpanded(tile);
    }

    _setupMobileSwipe() {
      if (!this._tilesEl) return;
      this._tilesEl.addEventListener('scroll', this._onTilesScroll, { passive: true });
    }

    _teardownMobileSwipe() {
      if (this._tilesEl) {
        this._tilesEl.removeEventListener('scroll', this._onTilesScroll);
      }
    }

    _onTilesScroll() {
      if (!this._mobileTicking) {
        this._mobileTicking = true;
        requestAnimationFrame(() => {
          this._updateMobilePagination();
          this._mobileTicking = false;
        });
      }
    }

    _updateMobilePagination() {
      if (!this._tilesEl || !this._allTiles.length) return;
      const scrollLeft = this._tilesEl.scrollLeft;
      const firstTile = this._allTiles[0];
      const tileWidth = firstTile.offsetWidth;
      const gap = parseFloat(getComputedStyle(this._tilesEl).gap) || 0;
      let currentIndex = Math.round(scrollLeft / (tileWidth + gap));
      currentIndex = Math.max(0, Math.min(this._allTiles.length - 1, currentIndex));

      this._allTiles.forEach((t, i) => {
        t.classList.toggle('is-active', i === currentIndex);
        if (i !== currentIndex && t.classList.contains('is-expanded')) this._closeExpanded(t);
      });

      /* AAS-5264: settle the entry morph the moment the strip leaves the first
         tile, not on the next vertical scroll. Clearing the morph's inline
         padding changes the scroller's geometry and nudges scrollLeft by a few
         px — during a swipe that is invisible under the gesture's own momentum,
         whereas later it reads as the strip twitching on its own. */
      if (this._isMobile && !this._isOnFirstTile() && !this._entrySettled) {
        this._cleanupEntry();
        this._entrySettled = true;
      }

      this._rollPagination(currentIndex);
    }

    _setupScrollHijack() {
      const tileCount = this._allTiles.length;
      if (tileCount <= 1 || !this._stickyEl) return;

      // 1) Reset to natural CSS state so the chrome measurement isn't polluted
      //    by entry-animation inlines or a prior --ct-chrome-height value.
      this.style.removeProperty('--ct-chrome-height');
      this._cleanupEntry();

      // 1b) Lock-anchor mode (AAS-5013). Top-pinning keeps the headline visible
      //     but budgets the tile from what's left of vh below the site header +
      //     section chrome — on short viewports (a 150%-scaled 1080p Windows
      //     laptop reports ~1250x583 CSS) that starves the tile until the
      //     expanded-panel copy can't fit. On wide viewports (aspect > 1.5:
      //     laptops/desktops; the threshold sits in the gap between tablet-
      //     landscape ~1.45 and 16:10 ~1.77 viewport ratios) bottom-anchor
      //     instead: let the headline scroll off and pin the wrapper with its
      //     bottom 80px above the viewport bottom. The modifier class switches
      //     the CSS width cap BEFORE the measurements below.
      this._bottomLock = window.innerWidth / window.innerHeight > 1.5;
      this.classList.toggle('core-technologies--bottom-lock', this._bottomLock);

      // 2) chromeHeight = everything in the sticky wrapper EXCEPT the tile itself
      //    (section header + tiles-container padding). Independent of tile size.
      const chromeHeight = this._stickyEl.offsetHeight - this._tile.offsetHeight;

      // 3) Expose to CSS. The min() expression on .core-technologies__tile now
      //    caps width if the natural tile would push the section taller than vh.
      this.style.setProperty('--ct-chrome-height', `${chromeHeight}px`);

      // 4) Measure post-cap dimensions for the carousel hijack distance.
      const secondTile = this._allTiles[1];
      const tileWidth = secondTile.offsetWidth;
      const gap = parseFloat(getComputedStyle(this._tilesEl).gap) || 0;

      const firstTile = this._allTiles[0];
      const inlineMl = firstTile.style.marginLeft;
      firstTile.style.marginLeft = '';
      const firstMargin = parseFloat(getComputedStyle(firstTile).marginLeft) || 0;
      firstTile.style.marginLeft = inlineMl;

      const viewportWidth = this._stickyEl.offsetWidth;
      const totalContentWidth = firstMargin + tileCount * tileWidth + (tileCount - 1) * gap;

      this._scrollPerTile = tileWidth + gap;
      this._totalScrollDistance = Math.max(0, totalContentWidth - viewportWidth);

      const stickyHeight = this._stickyEl.offsetHeight;
      this.style.height = `${stickyHeight + this._totalScrollDistance}px`;

      // Anchor the pin. stickyHeight is measured at the resting (State 2) tile
      // size — setup runs before the first _animateEntry and _onResize passes
      // through _cleanupEntry() above — so the anchor never bakes in the
      // full-width State 1 tile. The hijack trigger in _update() MUST equal
      // _stickyTop: if they diverge, the tiles drift vertically between
      // hijack start and pin engagement. AAS-5013
      if (this._bottomLock) {
        // The bottom inset comes from the same --ct-lock-gap-bottom the CSS
        // width caps use (declared on .core-technologies) — one source of
        // truth for the locked-stage geometry. Negative top is expected when
        // the wrapper is taller than the stage: the headline crops off the
        // top and the tile owns the viewport.
        const gapBottom = parseFloat(
          getComputedStyle(this).getPropertyValue('--ct-lock-gap-bottom')
        ) || 80;
        this._stickyTop = window.innerHeight - stickyHeight - gapBottom;
        this._stickyEl.style.top = `${this._stickyTop}px`;
      } else {
        // Squarish viewports: CSS top: var(--header-height) pins as before.
        // Trigger stays 0 (not header height) to preserve the shipped
        // top-anchored behavior exactly.
        this._stickyTop = 0;
        this._stickyEl.style.top = '';
      }

      // 5) Update entry-animation State 2 so the lerp ends at the actual rendered
      //    width (which may have been capped by the CSS min()).
      this._desktopState2.widthPct = (tileWidth / viewportWidth) * 100;

      // 6) Reserve the copy's resting (State 2) width up front so the headline /
      //    subheading never reflow as the tile narrows from the right during the
      //    entry morph. The content box is width:40% of the tile, so without a
      //    fixed width it rewraps every frame. Measured here at the resting size,
      //    so clearing the inline width on cleanup causes no jump. Desktop only;
      //    the mobile copy is width:auto. AAS-4800
      this._content = this._tile.querySelector('.core-technologies__tile-content');
      this._contentRestWidth = this._content
        ? this._content.getBoundingClientRect().width
        : 0;
    }

    _teardownScrollHijack() {
      this.style.height = '';
      this.style.removeProperty('--ct-chrome-height');
      this._totalScrollDistance = 0;
      this._scrollPerTile = 0;
      this._stickyTop = 0;
      this._bottomLock = false;
      this.classList.remove('core-technologies--bottom-lock');
      if (this._stickyEl) {
        this._stickyEl.style.top = '';
      }
      if (this._tilesEl) {
        this._tilesEl.style.transform = '';
      }
    }

    _onScroll() {
      if (!this._ticking) {
        this._ticking = true;
        this._raf = requestAnimationFrame(() => {
          this._update();
          this._ticking = false;
        });
      }
    }

    _onResize() {
      const wasMobile = this._isMobile;
      this._isMobile = this._mobileQuery.matches;
      if (wasMobile !== this._isMobile) {
        this._media = this._resolveMedia(this._tile);
        if (this._isMobile) {
          this._teardownScrollHijack();
          this._setupMobileSwipe();
        } else {
          this._teardownMobileSwipe();
          this._setupScrollHijack();
        }
      } else if (!this._isMobile) {
        this._setupScrollHijack();
      }
      this._update();

      // Re-anchor any open panel AFTER _update() so the measurement sees the
      // final rendered geometry (post re-cap, post lock-mode re-branch, post
      // entry inlines) — repositioning before it would snapshot a layout that
      // the update pass is about to change again. AAS-5013
      if (this._allTiles) {
        this._allTiles.forEach((tile) => {
          if (tile.classList.contains('is-expanded')) this._positionExpanded(tile);
        });
      }
    }

    _update() {
      const rect = this.getBoundingClientRect();
      const vh = window.innerHeight;
      const halfVh = vh * 0.5;

      // On short wide viewports _stickyTop is negative and the pin engages
      // after rect.top passes 0, so the default entry window (ends at 0) is
      // untouched. But on tall wide viewports the bottom anchor can sit BELOW
      // the viewport top (_stickyTop > 0) — shift the entry window up so the
      // morph completes exactly when the pin + hijack engage, instead of the
      // two animating over each other. AAS-5013
      const entryEnd = Math.max(0, this._stickyTop);
      let entryProgress = 1 - Math.max(0, rect.top - entryEnd) / halfVh;
      entryProgress = Math.max(0, Math.min(1, entryProgress));

      /* AAS-5264: on mobile the entry morph belongs to the FIRST tile only.
         It resizes that tile and animates the SCROLLER's own padding-left (see
         _animateEntry), so replaying it while the strip is swiped to another
         tile drags the content sideways under the reader — the glitch seen when
         scrolling back up after swiping to the last tile.
         Once the strip has left the first tile the morph is settled to its
         resting state and left alone; swiping back hands it over again. */
      if (this._isMobile && !this._isOnFirstTile()) {
        if (!this._entrySettled) {
          this._cleanupEntry();
          this._entrySettled = true;
        }
      } else {
        this._entrySettled = false;
        this._animateEntry(entryProgress);
      }

      if (this._isMobile) return;

      if (this._totalScrollDistance > 0) {
        // The hijack starts the instant the sticky pin engages (rect.top ==
        // _stickyTop in bottom-lock mode, 0 in top mode) — sharing the anchor
        // value is what prevents vertical drift during the lock. AAS-5013
        const triggerY = this._bottomLock ? this._stickyTop : 0;
        if (rect.top <= triggerY) {
          const carouselProgress = Math.min(1, (triggerY - rect.top) / this._totalScrollDistance);
          this._animateCarousel(carouselProgress);
        } else {
          this._resetCarousel();
        }
      }
    }

    _animateCarousel(progress) {
      if (!this._tilesEl || !this._allTiles.length) return;
      const translateX = -progress * this._totalScrollDistance;
      this._tilesEl.style.transform = `translateX(${translateX}px)`;

      let currentIndex = Math.round(progress * (this._allTiles.length - 1));
      currentIndex = Math.max(0, Math.min(this._allTiles.length - 1, currentIndex));
      this._allTiles.forEach((t, i) => {
        const isActive = i === currentIndex;
        t.classList.toggle('is-active', isActive);
        if (!isActive && t.classList.contains('is-expanded')) this._closeExpanded(t);
      });
      this._rollPagination(currentIndex);
    }

    _rollPagination(newIndex) {
      if (newIndex === this._lastPaginationIndex) return;
      const el = this._currentEl;
      if (!el) return;

      const direction = newIndex > this._lastPaginationIndex ? 1 : -1;
      const oldText = String(this._lastPaginationIndex + 1).padStart(2, '0');
      const newText = String(newIndex + 1).padStart(2, '0');
      this._lastPaginationIndex = newIndex;

      if (this._rollTimeout) {
        clearTimeout(this._rollTimeout);
        this._rollTimeout = null;
      }

      el.innerHTML = '';
      const wrapper = document.createElement('span');
      wrapper.className = 'core-technologies__roll';

      const topSpan = document.createElement('span');
      const bottomSpan = document.createElement('span');

      if (direction > 0) {
        topSpan.textContent = oldText;
        bottomSpan.textContent = newText;
        wrapper.appendChild(topSpan);
        wrapper.appendChild(bottomSpan);
        el.appendChild(wrapper);
        wrapper.offsetHeight;
        wrapper.style.transform = 'translateY(-50%)';
      } else {
        topSpan.textContent = newText;
        bottomSpan.textContent = oldText;
        wrapper.appendChild(topSpan);
        wrapper.appendChild(bottomSpan);
        wrapper.style.transform = 'translateY(-50%)';
        el.appendChild(wrapper);
        wrapper.offsetHeight;
        wrapper.style.transform = 'translateY(0)';
      }

      this._rollTimeout = setTimeout(() => {
        el.textContent = newText;
        this._rollTimeout = null;
      }, 350);
    }

    _resetCarousel() {
      if (this._tilesEl) {
        this._tilesEl.style.transform = '';
      }
      if (this._rollTimeout) {
        clearTimeout(this._rollTimeout);
        this._rollTimeout = null;
      }
      if (this._currentEl) {
        this._currentEl.textContent = String(1).padStart(2, '0');
      }
      this._lastPaginationIndex = 0;
    }

    _animateEntry(progress) {
      if (!this._tile) return;

      const s1 = this._isMobile ? this._mobileState1 : this._desktopState1;
      const s2 = this._isMobile ? this._mobileState2 : this._desktopState2;

      const w = s1.widthPct + (s2.widthPct - s1.widthPct) * progress;
      const ml = s1.marginLeft + (s2.marginLeft - s1.marginLeft) * progress;
      const ar = s1.ar + (s2.ar - s1.ar) * progress;

      if (progress >= 1) {
        this._cleanupEntry();
        return;
      }

      const ms1 = this._isMobile ? this._mobileMedia1 : this._desktopMedia1;
      const ms2 = this._isMobile ? this._mobileMedia2 : this._desktopMedia2;
      const mW = ms1.w + (ms2.w - ms1.w) * progress;
      const mH = ms1.h + (ms2.h - ms1.h) * progress;
      const mL = ms1.l + (ms2.l - ms1.l) * progress;
      const mT = ms1.t + (ms2.t - ms1.t) * progress;

      this._tile.style.width = `${w}%`;
      this._tile.style.aspectRatio = String(ar);

      // Hold the copy at its reserved resting width so it never reflows mid-morph. AAS-4800
      if (!this._isMobile && this._content && this._contentRestWidth) {
        this._content.style.width = `${this._contentRestWidth}px`;
      }

      if (this._isMobile && this._tilesEl) {
        this._tilesEl.style.paddingLeft = `${ml}px`;
      } else {
        this._tile.style.marginLeft = `${ml}px`;
      }

      if (this._media) {
        this._media.style.width = `${mW}%`;
        this._media.style.height = `${mH}%`;
        this._media.style.left = `${mL}%`;
        this._media.style.top = `${mT}%`;
      }

      const tileHeight = `${this._tile.offsetHeight}px`;
      for (const tile of this._allTiles.slice(1)) {
        tile.style.height = tileHeight;
      }
    }

    /* Is the horizontal strip resting on the first tile? Measured from
       scrollLeft rather than the .is-active class: that class is updated by
       _updateMobilePagination on its own rAF, so during a swipe it can lag the
       scroll position by a frame — long enough for the entry morph to fire once
       against the wrong tile. Half a step is the same threshold the pagination
       rounding uses. */
    _isOnFirstTile() {
      if (!this._tilesEl || !this._allTiles || !this._allTiles.length) return true;
      const gap = parseFloat(getComputedStyle(this._tilesEl).gap) || 0;
      const step = this._allTiles[0].offsetWidth + gap;
      if (step <= 0) return true;
      return this._tilesEl.scrollLeft < step / 2;
    }

    _cleanupEntry() {
      if (!this._tile) return;
      if (this._content) this._content.style.width = '';
      this._tile.style.width = '';
      this._tile.style.marginLeft = '';
      this._tile.style.aspectRatio = '';
      if (this._media) {
        this._media.style.width = '';
        this._media.style.height = '';
        this._media.style.left = '';
        this._media.style.top = '';
      }
      for (const tile of this._allTiles.slice(1)) {
        tile.style.height = '';
      }
      if (this._tilesEl) {
        this._tilesEl.style.paddingLeft = '';
      }
    }
  }

  customElements.define('core-technologies', CoreTechnologies);
}
